import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { blogPosts } from '../data/blog-posts';

const BlogContainer = styled.div`
    min-height: 100vh;
    padding: 100px 20px 60px 20px;
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
`;

const ContentWrapper = styled.div`
    max-width: 900px;
    margin: 0 auto;
    position: relative;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 50px;
    position: relative;
`;

const Title = styled.h1`
    font-size: 3rem;
    margin-bottom: 15px;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.3rem;
    opacity: 0.9;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const Description = styled.p`
    font-size: 1rem;
    opacity: 0.8;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
`;

const PostList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    margin-top: 40px;
`;

const PostCard = styled(Link)`
    display: block;
    padding: 30px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease;
    text-decoration: none;
    color: white;
    position: relative;
    overflow: visible;

    &:hover {
        transform: translateY(-5px);
    }
`;

const PostTitle = styled.h2`
    font-size: 2rem;
    margin: 0 0 10px 0;
    color: white;
    padding-right: ${props => props.hasBadge ? '100px' : '0'};
    transition: padding-right 0.3s ease;

    @media (max-width: 768px) {
        font-size: 1.5rem;
        padding-right: ${props => props.hasBadge ? '80px' : '0'};
    }
`;

const PostMeta = styled.div`
    display: flex;
    gap: 15px;
    margin-bottom: 15px;
    font-size: 0.9rem;
    opacity: 0.7;
    flex-wrap: wrap;
`;

const PostExcerpt = styled.p`
    font-size: 1.1rem;
    line-height: 1.8;
    opacity: 0.9;
    margin-top: 15px;
`;

const Tags = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 15px;
    flex-wrap: wrap;
`;

const Tag = styled.span`
    padding: 5px 12px;
    background: rgba(155, 89, 182, 0.3);
    border-radius: 15px;
    font-size: 0.85rem;
`;

const WritingMethodBadge = styled.span`
    position: absolute;
    top: 15px;
    right: 15px;
    padding: 6px 20px 6px 16px;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: inline-block;
    white-space: nowrap;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: rotate(2deg);
    transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
    pointer-events: none;
    clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%);
    
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 8px 0 8px 8px;
        border-color: transparent transparent transparent rgba(255, 255, 255, 0.2);
    }

    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }

    @media (max-width: 768px) {
        top: 12px;
        right: 12px;
        font-size: 0.65rem;
        padding: 5px 16px 5px 12px;
    }
`;

const PostContent = styled.div`
    padding: 40px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    margin-top: 40px;

    & h1, & h2, & h3, & h4, & h5, & h6 {
        margin-top: 30px;
        margin-bottom: 15px;
        color: white;
    }

    & p {
        line-height: 1.8;
        margin-bottom: 15px;
        opacity: 0.9;
    }

    & code {
        background: rgba(0, 0, 0, 0.3);
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Courier New', monospace;
    }

    & pre {
        background: rgba(0, 0, 0, 0.3);
        padding: 20px;
        border-radius: 10px;
        overflow-x: auto;
        margin: 20px 0;

        & code {
            background: none;
            padding: 0;
        }
    }

    & ul, & ol {
        margin-left: 20px;
        margin-bottom: 15px;
    }

    & li {
        margin-bottom: 8px;
        opacity: 0.9;
    }

    & blockquote {
        border-left: 4px solid rgba(155, 89, 182, 0.5);
        padding-left: 20px;
        margin: 20px 0;
        opacity: 0.8;
        font-style: italic;
    }

    & a {
        color: #5dade2;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const BackButton = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    text-decoration: none;
    border-radius: 12px;
    margin-bottom: 30px;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: translateX(-5px);
    }
`;

const LoadingText = styled.p`
    text-align: center;
    opacity: 0.7;
    font-style: italic;
    margin: 40px 0;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 20px;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    margin-top: 40px;
`;

const BlogPage = () => {
    const { t, i18n } = useTranslation();
    const { postId } = useParams();
    const [markdownContent, setMarkdownContent] = useState('');
    const [loading, setLoading] = useState(false);

    const currentLanguage = i18n.language;
    const publishedPosts = blogPosts.filter(post => post.published);

    useEffect(() => {
        if (postId) {
            const post = blogPosts.find(p => p.id === postId);
            if (post) {
                loadMarkdown(post.markdownFile);
            }
        }
    }, [postId]);

    const loadMarkdown = async (filename) => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/blog/${filename}`);
            const text = await response.text();
            setMarkdownContent(text);
        } catch (error) {
            console.error('Error loading markdown:', error);
            setMarkdownContent('# Error\n\nFailed to load post content.');
        } finally {
            setLoading(false);
        }
    };

    const getPostTitle = (post) => {
        return currentLanguage === 'en' && post.titleEn ? post.titleEn : post.title;
    };

    const getPostSubtitle = (post) => {
        return currentLanguage === 'en' && post.subtitleEn ? post.subtitleEn : post.subtitle;
    };

    const getPostExcerpt = (post) => {
        return currentLanguage === 'en' && post.excerptEn ? post.excerptEn : post.excerpt;
    };

    const getPostCategory = (post) => {
        return currentLanguage === 'en' && post.categoryEn ? post.categoryEn : post.category;
    };

    const getWritingMethodLabel = (method) => {
        const labels = {
            'ai-generated': '全 AI 生成',
            'draft-ai-completed': '草稿 + AI 完稿',
            'outline-ai-generated': '大綱 + AI 生成'
        };
        const labelsEn = {
            'ai-generated': 'AI Generated',
            'draft-ai-completed': 'Draft + AI Completed',
            'outline-ai-generated': 'Outline + AI Generated'
        };
        return currentLanguage === 'en' 
            ? (labelsEn[method] || labels[method] || '')
            : (labels[method] || '');
    };

    if (postId) {
        const post = blogPosts.find(p => p.id === postId);
        if (!post) {
            return (
                <BlogContainer>
                    <ContentWrapper>
                        <EmptyState>
                            <p>Post not found</p>
                            <BackButton to="/blog">← Back to Blog</BackButton>
                        </EmptyState>
                    </ContentWrapper>
                </BlogContainer>
            );
        }

        return (
            <BlogContainer>
                <ContentWrapper>
                    <BackButton to="/blog">← {t('blog.back', '返回網誌')}</BackButton>
                    <Header>
                        <Title>{getPostTitle(post)}</Title>
                        {getPostSubtitle(post) && <Subtitle>{getPostSubtitle(post)}</Subtitle>}
                        <PostMeta>
                            <span>{post.date}</span>
                            <span>•</span>
                            <span>{post.author}</span>
                            <span>•</span>
                            <span>{getPostCategory(post)}</span>
                        </PostMeta>
                        {post.writingMethod && (
                            <WritingMethodBadge>
                                {getWritingMethodLabel(post.writingMethod)}
                            </WritingMethodBadge>
                        )}
                        {post.tags && post.tags.length > 0 && (
                            <Tags>
                                {post.tags.map((tag, index) => (
                                    <Tag key={index}>{tag}</Tag>
                                ))}
                            </Tags>
                        )}
                    </Header>
                    {loading ? (
                        <LoadingText>{t('blog.loading', '載入中...')}</LoadingText>
                    ) : (
                        <PostContent>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {markdownContent}
                            </ReactMarkdown>
                        </PostContent>
                    )}
                </ContentWrapper>
            </BlogContainer>
        );
    }

    return (
        <BlogContainer>
            <ContentWrapper>
                <Header>
                    <Title>{t('blog.title')}</Title>
                    <Subtitle>{t('blog.subtitle')}</Subtitle>
                    <Description>
                        {t('blog.description')}
                    </Description>
                </Header>

                {publishedPosts.length === 0 ? (
                    <EmptyState>
                        <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.7 }}>📝</div>
                        <p style={{ fontSize: '1.2rem', opacity: 0.9 }}>
                            {t('blog.comingSoon')}
                        </p>
                    </EmptyState>
                ) : (
                    <PostList>
                        {publishedPosts.map((post) => (
                            <PostCard key={post.id} to={`/blog/${post.id}`}>
                                {post.writingMethod && (
                                    <WritingMethodBadge>
                                        {getWritingMethodLabel(post.writingMethod)}
                                    </WritingMethodBadge>
                                )}
                                <PostTitle hasBadge={!!post.writingMethod}>{getPostTitle(post)}</PostTitle>
                                {getPostSubtitle(post) && <Subtitle>{getPostSubtitle(post)}</Subtitle>}
                                <PostMeta>
                                    <span>{post.date}</span>
                                    <span>•</span>
                                    <span>{post.author}</span>
                                    <span>•</span>
                                    <span>{getPostCategory(post)}</span>
                                </PostMeta>
                                {getPostExcerpt(post) && (
                                    <PostExcerpt>{getPostExcerpt(post)}</PostExcerpt>
                                )}
                                {post.tags && post.tags.length > 0 && (
                                    <Tags>
                                        {post.tags.map((tag, index) => (
                                            <Tag key={index}>{tag}</Tag>
                                        ))}
                                    </Tags>
                                )}
                            </PostCard>
                        ))}
                    </PostList>
                )}
            </ContentWrapper>
        </BlogContainer>
    );
};

export default BlogPage;

